<template>
  <button
    ref="rootRef"
    tabindex="0"
    :class="[
      $style.workspaceItem,
      {
        [$style.selected]: selected,
        [$style.highlighted]: highlighted,
        [$style.focused]: focused,
      },
    ]"
    :style="{ paddingLeft: paddingLeft }"
    @click="$emit('click')"
  >
    <div :class="$style.inner">
      <div :class="$style.avatarAndTitles">
        <slot name="avatar">
          <SCAAvatar
            size="small"
            shape="square"
            :appearance="selected || focused ? 'blue' : 'white'"
            :bordered="!selected && !focused"
            :initials="convertNameToInitials(title)"
          >
            <SCASvgIcon v-if="icon" :icon="icon" :class="$style.avatarIcon" />
          </SCAAvatar>
        </slot>

        <div :class="$style.titles">
          <Transition name="slide-scale" mode="out-in">
            <div :key="title" ref="titleRef" :class="$style.title">{{ title }}</div>
          </Transition>
          <SCATooltip v-if="isTitleTruncated" :target="rootRef" :text="title" />
          <Transition name="slide-scale" mode="out-in">
            <div :key="subtitle" :class="$style.subtitle">{{ subtitle }}</div>
          </Transition>
        </div>
      </div>

      <div :class="$style.expanderIconContainer">
        <SCASvgIcon
          v-if="expandable"
          icon="phosphor/caret-down"
          :class="[$style.expanderIcon, { [$style.expandedIcon]: expanded }]"
          size="16px"
          @click.stop="$emit('toggle-expand')"
        />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { convertNameToInitials } from "@scayla/core";
import { computed, ref } from "vue";

import type { IconName } from "./icons/iconNames.generated";
import SCAAvatar from "./SCAAvatar.vue";
import SCASvgIcon from "./SCASvgIcon.vue";
import SCATooltip from "./SCATooltip.vue";
import { useIsTruncated } from "./useIsTruncated";

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle: string;
    selected?: boolean;
    icon?: IconName;
    expandable?: boolean;
    expanded?: boolean;
    highlighted?: boolean;
    focused?: boolean;
    level?: number;
  }>(),
  {
    selected: false,
    icon: undefined,
    expandable: false,
    expanded: false,
    highlighted: false,
    focused: false,
    level: 0,
  },
);

const paddingLeft = computed(() => {
  const basePadding = 4;
  const indentPerLevel = 28;
  return `${basePadding + props.level * indentPerLevel}px`;
});

const rootRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);
const isTitleTruncated = useIsTruncated(titleRef);

defineEmits<{
  click: [];
  "toggle-expand": [];
}>();
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.workspaceItem {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  height: 36px;
  padding: var(--space-component-padding-xSmall);
  border-radius: var(--border-radius-large);
  width: 100%;
  box-sizing: border-box;

  .inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1;
    min-width: 0;
    padding-right: var(--space-component-padding-xSmall);
  }

  .avatarIcon {
    @include IconMixins.icon-color(var(--color-semantic-neutral-0));
  }

  .expanderIconContainer {
    width: var(--dimension-250);
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .expanderIcon {
    @include IconMixins.icon-color(var(--color-semantic-neutral-0));
    transition: transform 0.3s ease;
  }

  .expandedIcon {
    transform: rotate(180deg);
  }

  &.selected {
    .avatarIcon {
      @include IconMixins.icon-color(white);
    }

    .expanderIcon {
      @include IconMixins.icon-color(var(--color-content-brand-primary));
    }

    color: var(--color-interaction-default-active);
    background-color: var(--color-action-primary-subtle-selected);
  }

  &.highlighted {
    background-color: var(--color-background-neutral-onSubtle);
  }

  &:hover {
    background-color: var(--color-action-ghost-hover);
  }

  &:active {
    background-color: var(--color-action-ghost-active);
  }

  &.focused {
    background-color: var(--color-action-inverted-normal);
    box-shadow: 0px 1px 2px 0px var(--color-semantic-neutralA-p3);

    .avatarIcon {
      @include IconMixins.icon-color(white);
    }

    .expanderIcon {
      @include IconMixins.icon-color(var(--color-content-brand-primary));
    }
  }
}

.avatarAndTitles {
  display: flex;
  align-items: center;
  gap: var(--space-component-gap-small);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.titles {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  overflow: hidden;
  padding-right: var(--space-component-padding-xSmall);
}

.title {
  @include FigmaTypoMixins.typo-body-strong-medium;
  color: var(--color-content-secondary);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.subtitle {
  @include FigmaTypoMixins.typo-body-xsmall-tight;
  color: var(--color-content-disabled);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:global(.slide-scale-enter-active),
:global(.slide-scale-leave-active) {
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;
}

:global(.slide-scale-enter-from),
:global(.slide-scale-leave-to) {
  opacity: 0;
  transform: translateY(-10px) scale(1);
}

:global(.slide-scale-enter-to),
:global(.slide-scale-leave-from) {
  opacity: 1;
  transform: translateY(0) scale(1);
}
</style>
